import type {
  MarketDataAssetClassRequiredBody,
  ResolveInstrumentResponse,
} from "../types/instruments";
import type { MarketDataUnresolvedDetail } from "../types/instrumentSetup";

export function isMarketDataAssetClassRequiredBody(
  value: unknown
): value is MarketDataAssetClassRequiredBody {
  if (!value || typeof value !== "object") return false;
  const o = value as Record<string, unknown>;
  return o.error === "market_data_asset_class_required";
}

export function parseMarketDataAssetClassRequired(
  value: unknown
): MarketDataAssetClassRequiredBody | null {
  if (!isMarketDataAssetClassRequiredBody(value)) return null;
  return value;
}

function unwrapApiDetail(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const detail = (value as { detail?: unknown }).detail;
  if (detail && typeof detail === "object") return detail;
  return value;
}

/** FastAPI may wrap the object in `detail`. */
export function parseMarketDataAssetClassRequiredFromApiError(
  value: unknown
): MarketDataAssetClassRequiredBody | null {
  const direct = parseMarketDataAssetClassRequired(value);
  if (direct) return direct;
  const unwrapped = unwrapApiDetail(value);
  if (typeof unwrapped === "string") return null;
  return parseMarketDataAssetClassRequired(unwrapped);
}

const MARKET_DATA_ERROR_CODES = new Set([
  "market_data_quote_variant_required",
  "market_data_asset_class_required",
  "market_data_not_supported",
  "market_data_resolution_failed",
  "market_data_connector_required",
  "market_data_preference_unavailable",
]);

export function isMarketDataUnresolvedDetail(value: unknown): value is MarketDataUnresolvedDetail {
  if (!value || typeof value !== "object") return false;
  const error = (value as { error?: unknown }).error;
  return typeof error === "string" && MARKET_DATA_ERROR_CODES.has(error);
}

export function parseMarketDataUnresolvedDetail(value: unknown): MarketDataUnresolvedDetail | null {
  if (isMarketDataUnresolvedDetail(value)) return value;
  const unwrapped = unwrapApiDetail(value);
  if (isMarketDataUnresolvedDetail(unwrapped)) return unwrapped;

  if (value && typeof value === "object") {
    const o = value as Record<string, unknown>;
    const error = o.error;
    if (typeof error === "string" && MARKET_DATA_ERROR_CODES.has(error)) {
      const details =
        o.details && typeof o.details === "object" && !Array.isArray(o.details)
          ? (o.details as Record<string, unknown>)
          : {};
      return {
        ...(details as unknown as MarketDataUnresolvedDetail),
        error,
        message:
          typeof o.message === "string"
            ? o.message
            : typeof details.message === "string"
              ? details.message
              : undefined,
      };
    }
  }

  return null;
}

export class MarketDataUnresolvedError extends Error {
  status: number;
  body: MarketDataUnresolvedDetail;

  constructor(body: MarketDataUnresolvedDetail, status = 422) {
    super(body.message || "Market data setup required for this symbol.");
    this.name = "MarketDataUnresolvedError";
    this.status = status;
    this.body = body;
  }
}

/** When BE returns 422 with a plain string `detail`, recover enough shape to confirm or retry. */
export function synthesizeMarketDataAssetClassRequired(
  symbol: string,
  options?: {
    suggestedAssetClass?: string;
    brokerName?: string | null;
    errorMessage?: string;
  }
): MarketDataAssetClassRequiredBody | null {
  const suggested = options?.suggestedAssetClass?.trim().toLowerCase();
  if (!suggested) return null;

  const msg = (options?.errorMessage || "").toLowerCase();
  if (msg && !msg.includes("asset class")) return null;

  return {
    error: "market_data_asset_class_required",
    symbol,
    suggested_asset_class: suggested,
    valid_asset_classes: [suggested],
  };
}

export function isMarketDataConnectGateDetail(
  value: unknown
): value is MarketDataUnresolvedDetail {
  const detail = parseMarketDataUnresolvedDetail(value);
  if (!detail) return false;
  return (
    detail.error === "market_data_connector_required" ||
    detail.chart_unavailable_reason === "market_data_connector_required" ||
    detail.error === "market_data_preference_unavailable" ||
    detail.chart_unavailable_reason === "market_data_preference_unavailable"
  );
}

export function isMarketDataPreferenceUnavailableDetail(
  value: unknown
): value is MarketDataUnresolvedDetail {
  const detail = parseMarketDataUnresolvedDetail(value);
  if (!detail) return false;
  return (
    detail.error === "market_data_preference_unavailable" ||
    detail.chart_unavailable_reason === "market_data_preference_unavailable"
  );
}

export function isMarketDataConnectorRequiredDetail(
  value: unknown
): value is MarketDataUnresolvedDetail {
  return isMarketDataConnectGateDetail(value);
}

export function isMarketDataBrokerConnectConsentRequired(
  value: unknown
): { broker: string; message?: string } | null {
  const detail = parseMarketDataUnresolvedDetail(value);
  if (!detail) return null;
  if (detail.consent_layer !== "broker_connect" && detail.consent_layer !== "connect") {
    return null;
  }
  const broker = detail.consent_broker?.trim();
  if (!broker) return null;
  return { broker, message: detail.message };
}

export function marketDataUnresolvedToAssetClassPayload(
  detail: MarketDataUnresolvedDetail
): MarketDataAssetClassRequiredBody | null {
  if (detail.error === "market_data_asset_class_required" && detail.symbol) {
    return {
      error: "market_data_asset_class_required",
      symbol: detail.symbol,
      suggested_asset_class: detail.suggested_asset_class,
      valid_asset_classes: detail.valid_asset_classes,
      candidate_brokers: detail.candidate_brokers,
      market_data_sources: detail.market_data_sources,
    };
  }
  return null;
}

export function instrumentSetupRowFromUnresolved(
  detail: MarketDataUnresolvedDetail,
  tradeSymbol: string
): Partial<ResolveInstrumentResponse> {
  return {
    symbol: detail.symbol ?? tradeSymbol,
    requires_quote_variant_choice: detail.requires_quote_variant_choice,
    requires_asset_class_confirmation: detail.requires_asset_class_confirmation,
    suggested_asset_class: detail.suggested_asset_class,
    valid_asset_classes: detail.valid_asset_classes,
    market_data_sources: detail.market_data_sources,
    candidate_brokers: detail.candidate_brokers,
  };
}
